<template>
  <div class="an">

    <!-- main header -->
    <MainHeader
      :hero-icon="heroIcon"
      :hero-title="heroTitle"
      :hero-subtitle="heroSubtitle"
      :back-button="false"
    />
    <!-- loading -->
    <div
      v-if="loader"
      class="an-state"
    >
      <b-spinner
        type="grow"
        variant="primary"
        label="Loading..."
      />
      <p>Loading analysis...</p>
    </div>

    <!-- error -->
    <div
      v-else-if="error"
      class="an-state an-state--error"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="42"
      />
      <h5>We could not load the analysis</h5>
      <p>Please check your connection and try again.</p>
      <button
        type="button"
        class="an-retry"
        @click="getAnalysis"
      >
        <feather-icon
          icon="RefreshCwIcon"
          size="15"
        />
        Retry
      </button>
    </div>

    <template v-else>
      <!-- landing: choose a view -->
      <transition
        name="an-fade"
        mode="out-in"
      >
        <div
          v-if="!activeView"
          key="landing"
          class="an-choices"
        >
          <div
            v-for="view in views"
            :key="view.key"
            class="an-choice"
            :class="`an-choice--${view.key}`"
            role="button"
            tabindex="0"
            @click="activeView = view.key"
            @keyup.enter="activeView = view.key"
          >
            <span class="an-choice__bubble an-choice__bubble--1" />
            <span class="an-choice__bubble an-choice__bubble--2" />

            <div class="an-choice__icon">
              <feather-icon
                :icon="view.icon"
                size="30"
              />
            </div>

            <h3>{{ view.title }}</h3>
            <p>{{ view.description }}</p>

            <div class="an-choice__stats">
              <div
                v-for="stat in view.stats"
                :key="stat.label"
                class="an-choice__stat"
              >
                <feather-icon
                  :icon="stat.icon"
                  size="16"
                />
                <strong>{{ stat.value }}</strong>
                <span>{{ stat.label }}</span>
              </div>
            </div>

            <span class="an-choice__cta">
              Open
              <feather-icon
                icon="ArrowRightIcon"
                size="16"
              />
            </span>
          </div>
        </div>

        <!-- selected view -->
        <div
          v-else
          :key="activeView"
          class="an-view"
        >
          <div class="an-tabs">
            <button
              v-for="view in views"
              :key="view.key"
              type="button"
              class="an-tab"
              :class="{ 'an-tab--active': activeView === view.key }"
              @click="activeView = view.key"
            >
              <feather-icon
                :icon="view.icon"
                size="16"
              />
              {{ view.title }}
            </button>
          </div>

          <requests-analysis
            v-if="activeView === 'requests'"
            :requests-analysis-data="requestsAnalysisData"
          />
          <users-analysis
            v-else
            :user-analysis-data="usersAnalysisData"
          />
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'
import { formatNumber, toNumber } from '@/components/analysis-table/analysisTheme'
import RequestsAnalysis from './RequestsAnalysis.vue'
import UsersAnalysis from './UsersAnalysis.vue'
import MainHeader from '@/components/main-header/MainHeader.vue'

export default {
  components: {
    RequestsAnalysis,
    UsersAnalysis,
    MainHeader,
  },
  data() {
    return {
      requestsAnalysisData: [],
      usersAnalysisData: [],
      loader: false,
      error: false,
      activeView: null, // 'requests' | 'users' | null
    }
  },
  computed: {
    views() {
      const sources = this.requestsAnalysisData
      const engineers = this.usersAnalysisData
      const sum = (list, key) => list.reduce((acc, item) => acc + toNumber(item[key]), 0)

      return [
        {
          key: 'requests',
          title: 'Requests Analysis',
          icon: 'BarChart2Icon',
          description: 'See how requests are distributed across every source: volume, issued certificates, pending and late work.',
          stats: [
            { icon: 'DatabaseIcon', label: 'Sources', value: formatNumber(sources.length) },
            { icon: 'FileTextIcon', label: 'Requests', value: formatNumber(sum(sources, 'total_count')) },
            { icon: 'ClockIcon', label: 'Pending', value: formatNumber(sum(sources, 'total_pending')) },
          ],
        },
        {
          key: 'users',
          title: 'Users Analysis',
          icon: 'UsersIcon',
          description: 'Follow the workload of every engineer, filter by source and quickly spot who has late requests.',
          stats: [
            { icon: 'UsersIcon', label: 'Engineers', value: formatNumber(engineers.length) },
            { icon: 'ActivityIcon', label: 'Total load', value: formatNumber(sum(engineers, 'total_load')) },
            { icon: 'ClockIcon', label: 'Pending', value: formatNumber(sum(engineers, 'total_pending')) },
          ],
        },
      ]
    },
    currentView() {
      return this.views.find(v => v.key === this.activeView)
    },
    heroIcon() {
      return this.currentView ? this.currentView.icon : 'PieChartIcon'
    },
    heroTitle() {
      return this.currentView ? this.currentView.title : 'Analysis'
    },
    heroSubtitle() {
      return this.currentView
        ? 'Live numbers across all sources.'
        : 'Choose what you would like to analyse.'
    },
  },
  mounted() {
    this.getAnalysis()
  },
  methods: {
    getAnalysis() {
      this.loader = true
      this.error = false

      axios.get('/analysis').then(res => {
        const analysis = decryptData(res.data.payload).data?.analysis || {}
        this.requestsAnalysisData = analysis.requests_analysis || []
        this.usersAnalysisData = analysis.users_analysis || []
      }).catch(() => {
        this.error = true
      }).finally(() => {
        this.loader = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$ink: #1d2b44;
$muted: #6b7a90;
$line: #d5e2f2;
$blue: #1c84f2;

.an {
  width: 100%;
  padding-bottom: 24px;
}

/* states */
.an-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: $muted;

  &--error {
    color: #c93a3f;

    h5 {
      color: $ink;
    }

    p {
      color: $muted;
    }
  }
}

.an-retry {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 20px;
  font-weight: 600;
  color: #fff;
  background: $blue;
  border: 0;
  border-radius: 12px;
  cursor: pointer;

  &:hover {
    background: #0a62e8;
  }
}

/* landing choices */
.an-choices {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 24px;
  max-width: 1000px;
  margin: 0 auto;
}

.an-choice {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 30px;
  overflow: hidden;
  color: $ink;
  background: linear-gradient(160deg, var(--tint) 0%, #eef4fb 75%);
  border: 1px solid var(--tint-line);
  border-radius: 22px;
  cursor: pointer;
  outline: none;
  box-shadow: 0 4px 16px rgba(31, 45, 61, 0.06);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, var(--accent-deep), var(--accent));
  }

  &--requests {
    --accent: #1c84f2;
    --accent-deep: #0a62e8;
    --tint: #eaf4ff;
    --tint-line: #d4e7fb;
    --chip: #f6f9fd;
  }

  &--users {
    --accent: #4f78ef;
    --accent-deep: #3a54c4;
    --tint: #eef1ff;
    --tint-line: #dbe1fa;
    --chip: #f6f9fd;
  }

  &:hover,
  &:focus-visible {
    transform: translateY(-5px);
    border-color: var(--accent);
    box-shadow: 0 14px 30px rgba(31, 45, 61, 0.11);

    .an-choice__cta svg {
      transform: translateX(5px);
    }
  }

  &__bubble {
    position: absolute;
    background: var(--tint-line);
    opacity: 0.55;
    border-radius: 50%;

    &--1 {
      right: -50px;
      top: -50px;
      width: 190px;
      height: 190px;
    }

    &--2 {
      right: 40px;
      bottom: -80px;
      width: 150px;
      height: 150px;
    }
  }

  &__icon {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 62px;
    height: 62px;
    color: #fff;
    background: linear-gradient(135deg, var(--accent-deep), var(--accent));
    border-radius: 18px;
    box-shadow: 0 8px 18px rgba(31, 45, 61, 0.14);
  }

  h3 {
    position: relative;
    margin: 6px 0 0;
    font-weight: 800;
    color: $ink;
  }

  p {
    position: relative;
    margin: 0;
    line-height: 1.6;
    color: $muted;
  }

  &__stats {
    position: relative;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-top: 8px;
  }

  &__stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 11px 12px;
    background: var(--chip);
    border: 1px solid var(--tint-line);
    border-radius: 14px;

    svg {
      color: var(--accent);
    }

    strong {
      font-size: 1.15rem;
      color: $ink;
    }

    span {
      font-size: 0.7rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: $muted;
    }
  }

  &__cta {
    position: relative;
    display: inline-flex;
    align-items: center;
    align-self: flex-end;
    gap: 6px;
    margin-top: 4px;
    font-weight: 700;
    color: var(--accent-deep);

    svg {
      transition: transform 0.2s;
    }
  }
}
/* selected view */
.an-tabs {
  display: inline-flex;
  gap: 6px;
  padding: 5px;
  margin-bottom: 22px;
  background: #eef4fb;
  border: 1px solid $line;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(31, 45, 61, 0.04);
}

.an-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  font-weight: 600;
  color: #44546a;
  background: transparent;
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: $blue;
    background: #e3ecf8;
  }

  &--active,
  &--active:hover {
    color: #fff;
    background: $blue;
    box-shadow: 0 4px 12px rgba(10, 98, 232, 0.35);
  }
}

/* transition */
.an-fade-enter-active,
.an-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.an-fade-enter,
.an-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
